import {useSettings} from '@common/core/settings/use-settings';
import React, {Fragment} from 'react';
import {useAuth} from '@common/auth/use-auth';
import {ToastContainer} from '@common/ui/toast/toast-container';
import {AppearanceListener} from '@common/admin/appearance/commands/appearance-listener';
import {CookieNotice} from '@common/ui/cookie-notice/cookie-notice';
import {AuthRoute} from '@common/auth/guards/auth-route';
import {DynamicHomepage} from '@common/ui/dynamic-homepage';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import {AuthRoutes} from '@common/auth/auth-routes';
import {BillingRoutes} from '@common/billing/billing-routes';
import {NotificationRoutes} from '@common/notifications/notification-routes';
import {ContactUsPage} from '@common/contact/contact-us-page';
import {PublicDocsLayout} from '@app/docs/public-docs-layout';
import {PublicBlogLayout} from '@app/blog/public-blog-layout';
import {CustomPageLayout} from '@common/custom-page/custom-page-layout';
import {DocsCatchAllRoute} from '@app/docs/docs-catch-all-route';
import {DialogStoreOutlet} from '@common/ui/overlays/store/dialog-store-outlet';
import {EmailVerificationPage} from '@common/auth/ui/email-verification-page/email-verification-page';
import {Navigate, Route, Routes} from 'react-router-dom';
import {LandingPage} from '@app/landing/landing-page';
import {GuestRoute} from '@common/auth/guards/guest-route';
import {TestLocalePage} from '@common/i18n/test-locale-page';
import {SpaPageviewTracker} from '@app/analytics/spa-pageview-tracker';
import {ActiveWorkspaceProvider} from '@common/workspace/active-workspace-id-context';
import {useIsStudioFeatureEnabled} from '@app/studio/is-studio-feature-enabled';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';

const DashboardRoutes = React.lazy(
  () => import('./dashboard/dashboard-routes'),
);
const EditorPage = React.lazy(() => import('@app/editor/editor-page'));
const StudioWorkspacePage = React.lazy(() =>
  import('@app/studio/studio-workspace-page').then(m => ({
    default: m.StudioWorkspacePage,
  })),
);
const SitePlayerPage = React.lazy(
  () => import('@app/site-player/site-player-page'),
);
const AdminRoutes = React.lazy(() => import('@common/admin/admin-routes'));
const SwaggerApiDocs = React.lazy(
  () => import('@common/swagger/swagger-api-docs-page'),
);

/** Content Studio workspace — STUDIO_ENABLED. Added: 2026-09-11. Changed: 2026-09-22. */
function StudioWorkspaceGate() {
  const enabled = useIsStudioFeatureEnabled();
  if (!enabled) {
    return <NotFoundPage />;
  }
  return <StudioWorkspacePage />;
}

export function AppRoutes() {
  const {billing, notifications, require_email_confirmation, api} =
    useSettings();
  const {user, hasPermission, isLoggedIn} = useAuth();

  if (user != null && require_email_confirmation && !user.email_verified_at) {
    return (
      <Fragment>
        <ToastContainer />
        <Routes>
          <Route path="*" element={<EmailVerificationPage />} />
        </Routes>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <SpaPageviewTracker />
      <AppearanceListener />
      <CookieNotice />
      <ToastContainer />
      <Routes>
        <Route
          path="/"
          element={
            <DynamicHomepage
              homepageResolver={() => (
                <GuestRoute>
                  <LandingPage />
                </GuestRoute>
              )}
            />
          }
        />
        <Route
          path="/admin/*"
          element={
            <AuthRoute permission="admin.access">
              <React.Suspense fallback={<FullPageLoader screen />}>
                <AdminRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        <Route
          path="/dashboard/*"
          element={
            <AuthRoute>
              <React.Suspense fallback={<FullPageLoader screen />}>
                <DashboardRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        <Route
          path="/editor/:projectId"
          element={
            <AuthRoute>
              <ActiveWorkspaceProvider>
                <React.Suspense fallback={<FullPageLoader screen />}>
                  <EditorPage />
                </React.Suspense>
              </ActiveWorkspaceProvider>
            </AuthRoute>
          }
        />
        <Route
          path="/studio/:projectId"
          element={
            <AuthRoute>
              <ActiveWorkspaceProvider>
                <React.Suspense fallback={<FullPageLoader screen />}>
                  <StudioWorkspaceGate />
                </React.Suspense>
              </ActiveWorkspaceProvider>
            </AuthRoute>
          }
        />
        <Route
          path="/player"
          element={
            <AuthRoute>
              <ActiveWorkspaceProvider>
                <React.Suspense fallback={<FullPageLoader screen />}>
                  <SitePlayerPage />
                </React.Suspense>
              </ActiveWorkspaceProvider>
            </AuthRoute>
          }
        />
        {AuthRoutes}
        {billing.enable && BillingRoutes}
        {notifications.integrated && NotificationRoutes}
        {api?.integrated && hasPermission('api.access') && (
          <Route
            path="api-docs"
            element={
              <React.Suspense fallback={<FullPageLoader screen />}>
                <SwaggerApiDocs />
              </React.Suspense>
            }
          />
        )}
        <Route
          path="contact"
          element={
            isLoggedIn ? (
              <Navigate to="/dashboard/support" replace />
            ) : (
              <ContactUsPage />
            )
          }
        />
        <Route path="/docs">
          <Route index element={<PublicDocsLayout />} />
          <Route path="*" element={<PublicDocsLayout />} />
        </Route>
        <Route path="/blog" element={<PublicBlogLayout />} />
        <Route path="/blog/*" element={<PublicBlogLayout />} />
        <Route path="test/locale" element={<TestLocalePage />} />
        <Route path="pages/:pageSlug" element={<CustomPageLayout />} />
        <Route path="pages/:pageId/:pageSlug" element={<CustomPageLayout />} />
        <Route path="*" element={<DocsCatchAllRoute />} />
      </Routes>
      <DialogStoreOutlet />
    </Fragment>
  );
}
